import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import {
  Lock,
  Visibility,
  VisibilityOff,
  Security,
  Warning
} from '@mui/icons-material';


interface OSPanelPinAccessProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function OSPanelPinAccess({ open, onClose, onSuccess }: OSPanelPinAccessProps) {
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutTime, setLockoutTime] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [correctPin, setCorrectPin] = useState<string | null>(null);

  const MAX_ATTEMPTS = 5;
  const LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes in milliseconds

  useEffect(() => {
    // Get PIN from environment variable
    const envPin = import.meta.env.VITE_OSPANEL_PIN;
    if (envPin) {
      setCorrectPin(envPin);
    }
  }, []);

  useEffect(() => {
    // Check if user is locked out
    const storedLockoutTime = localStorage.getItem('ospanel_lockout_time');
    if (storedLockoutTime) {
      const lockoutTime = new Date(storedLockoutTime);
      const now = new Date();
      if (now.getTime() - lockoutTime.getTime() < LOCKOUT_DURATION) {
        setIsLocked(true);
        setLockoutTime(lockoutTime);
      } else {
        // Lockout expired
        localStorage.removeItem('ospanel_lockout_time');
        localStorage.removeItem('ospanel_attempts');
        setIsLocked(false);
        setAttempts(0);
      }
    }

    // Get stored attempts
    const storedAttempts = localStorage.getItem('ospanel_attempts');
    if (storedAttempts) {
      setAttempts(parseInt(storedAttempts));
    }
  }, []);

  const handlePinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Only allow numbers and limit to 7 characters
    if (/^\d{0,7}$/.test(value)) {
      setPin(value);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (!correctPin) {
      setError('PIN system not configured. Contact administrator.');
      return;
    }

    if (pin.length !== 7) {
      setError('PIN must be exactly 7 digits');
      return;
    }

    setLoading(true);
    setError(null);

    // Simulate a small delay for security
    await new Promise(resolve => setTimeout(resolve, 500));

    if (pin === correctPin) {
      // Success - reset attempts and allow access
      localStorage.removeItem('ospanel_attempts');
      localStorage.removeItem('ospanel_lockout_time');
      setAttempts(0);
      setIsLocked(false);
      setPin('');
      onSuccess();
    } else {
      // Failed attempt
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      localStorage.setItem('ospanel_attempts', newAttempts.toString());

      if (newAttempts >= MAX_ATTEMPTS) {
        // Lock out user
        const lockoutTime = new Date();
        localStorage.setItem('ospanel_lockout_time', lockoutTime.toISOString());
        setIsLocked(true);
        setLockoutTime(lockoutTime);
        setError(`Too many failed attempts. Account locked for 15 minutes.`);
      } else {
        const remainingAttempts = MAX_ATTEMPTS - newAttempts;
        setError(`Incorrect PIN. ${remainingAttempts} attempt${remainingAttempts !== 1 ? 's' : ''} remaining.`);
      }
      setPin('');
    }

    setLoading(false);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !loading && !isLocked) {
      handleSubmit();
    }
  };

  const getRemainingLockoutTime = () => {
    if (!lockoutTime) return 0;
    const now = new Date();
    const remaining = LOCKOUT_DURATION - (now.getTime() - lockoutTime.getTime());
    return Math.max(0, Math.ceil(remaining / 1000 / 60)); // Return minutes
  };

  const [lockoutMinutes, setLockoutMinutes] = useState(getRemainingLockoutTime());

  useEffect(() => {
    if (isLocked && lockoutTime) {
      const interval = setInterval(() => {
        const remaining = getRemainingLockoutTime();
        setLockoutMinutes(remaining);
        if (remaining <= 0) {
          setIsLocked(false);
          setAttempts(0);
          localStorage.removeItem('ospanel_lockout_time');
          localStorage.removeItem('ospanel_attempts');
          clearInterval(interval);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isLocked, lockoutTime]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        color: '#c62828',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1
      }}>
        <Security sx={{ fontSize: 28 }} />
        OS Panel Access
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the 7-digit security PIN to access the OS Panel
          </Typography>
          
          {isLocked ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Warning />
                <Typography>
                  Account locked. Please wait {lockoutMinutes} minute{lockoutMinutes !== 1 ? 's' : ''} before trying again.
                </Typography>
              </Box>
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Lock />
                <Typography>
                  {MAX_ATTEMPTS - attempts} attempt{MAX_ATTEMPTS - attempts !== 1 ? 's' : ''} remaining
                </Typography>
              </Box>
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            type={showPin ? 'text' : 'password'}
            value={pin}
            onChange={handlePinChange}
            onKeyPress={handleKeyPress}
            placeholder="Enter 7-digit PIN"
            disabled={isLocked || loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPin(!showPin)}
                    edge="end"
                    disabled={isLocked}
                  >
                    {showPin ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
              inputProps: {
                maxLength: 7,
                style: { 
                  textAlign: 'center',
                  fontSize: '1.2rem',
                  letterSpacing: '0.5em'
                }
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'white'
              }
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLocked || loading || pin.length !== 7}
          startIcon={loading ? <CircularProgress size={20} /> : <Security />}
          sx={{ 
            minWidth: 100,
            backgroundColor: '#c62828',
            '&:hover': {
              backgroundColor: '#b71c1c'
            }
          }}
        >
          {loading ? 'Verifying...' : 'Access'}
        </Button>
      </DialogActions>
    </Dialog>
  );
} 